import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import CouponServices from '../../services/externalServices/couponService';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ coupcode: '', totalCount: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // success | error | info | warning
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const data = await CouponServices.getCoupons();
      setCoupons(data);
    } catch (error) {
      showSnackbar('Error fetching coupons.', 'error');
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({ coupcode: '', totalCount: 0 });
    setIsEditing(false);
    setEditId(null);
  };

  const handleAddCoupon = async () => {
    if (!form.coupcode || form.totalCount <= 0) {
      showSnackbar('Please enter valid coupon details.', 'error');
      return;
    }
  
    try {
      const newCoupon = await CouponServices.addCoupon(form);
      console.log("newCoupon",newCoupon)
      if (newCoupon && newCoupon._id) {
        setCoupons((prev) => [...prev, newCoupon]);
        resetForm();
        showSnackbar('Coupon added successfully!', 'success');
      } 
      else if (newCoupon.error==="Unauthorized: Super Admin access required"){
        showSnackbar("Only Super admin can add the add the coupon",'error');
        // throw new Error("Only Super admin can add the add the coupon")
      }
        else {
        throw new Error('Invalid response received from the server.');
      }
    } catch (error) {
      // Check for duplicate coupon code error
     
      if (error.response) {
        // 409 Conflict is commonly used for duplicate entries
        showSnackbar('Coupon code already exists. Please use a different code.', 'error');
      } 
      // else if(error.response==="error"){
      // }
      else {
        showSnackbar('Error adding coupon.', 'error');
      }
      console.error('Error adding coupon:', error);
    }
  };
  

  const handleEditClick = (coupon) => {
    setForm({ coupcode: coupon.coupcode, totalCount: coupon.totalCount });
    setIsEditing(true);
    setEditId(coupon._id);
  };

  const handleUpdateCoupon = async () => {
    if (!form.coupcode || form.totalCount <= 0) {
      showSnackbar('Please enter valid coupon details.', 'error');
      return;
    }
    try {
      const updatedCoupon = await CouponServices.updateCoupon(editId, form);
     
      if(updatedCoupon.error){
        showSnackbar("Only Super admin can add the add the coupon",'error');
        return;
      }
      setCoupons((prev) =>
        prev.map((coupon) => (coupon._id === editId ? updatedCoupon : coupon))
      );
      resetForm();
      showSnackbar('Coupon updated successfully!', 'success');
    } catch (error) {
      showSnackbar('Error updating coupon.', 'error');
      console.error('Error updating coupon:', error);
    }
  };

  const handleDeleteCoupon = async (id) => {
    try {
      await CouponServices.deleteCoupon(id);
      setCoupons((prev) => prev.filter((coupon) => coupon._id !== id));
      showSnackbar('Coupon deleted successfully!', 'success');
    } catch (error) {
      showSnackbar('Error deleting coupon.', 'error');
      console.error('Error deleting coupon:', error);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Coupons
      </Typography>

      <Paper sx={{ padding: 2, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditing ? 'Edit Coupon' : 'Add Coupon'}
        </Typography>
        <form
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
          noValidate
        >
          <TextField
            label="Coupon Code"
            name="coupcode"
            value={form.coupcode}
            onChange={handleInputChange}
            variant="outlined"
            fullWidth
          />
          <TextField
            label="Total Count"
            name="totalCount"
            type="number"
            value={form.totalCount}
            onChange={handleInputChange}
            variant="outlined"
            fullWidth
          />
          <Button
            variant="contained"
            color="primary"
            onClick={isEditing ? handleUpdateCoupon : handleAddCoupon}
          >
            {isEditing ? 'Update' : 'Add'}
          </Button>
          {isEditing && (
            <Button variant="outlined" color="secondary" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </form>
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Coupon Code</TableCell>
                <TableCell>Total Count</TableCell>
                <TableCell>Used Count</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((coupon,index) => (
                <TableRow key={index}>
                  <TableCell>{coupon.coupcode}</TableCell>
                  <TableCell>{coupon.totalCount}</TableCell>
                  <TableCell>{coupon.usedCount}</TableCell>
                  <TableCell>
                    {new Date(coupon.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {new Date(coupon.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleEditClick(coupon)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteCoupon(coupon._id)}
                      sx={{ ml: 1 }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar for Success/Error Messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Coupons;
